import {autorun, makeAutoObservable, runInAction} from "mobx";
import {cubeModel} from "./cube";
import {occupancyModel} from "./occupancy";

const POST_ZERO_POLL_INTERVAL_MS = 5000;
const POST_ZERO_POLL_COUNT = 6;

class GracePeriodModel {
	private gracePeriodSecondsLeft = new Map<number, number>();
	private countDownInterval = NaN;
	private postZeroPollInterval = NaN;
	private postZeroPollsRemaining = 0;

	constructor() {
		makeAutoObservable<
			GracePeriodModel,
			"countDownInterval" | "postZeroPollInterval" | "postZeroPollsRemaining"
		>(this, {
			countDownInterval: false,
			postZeroPollInterval: false,
			postZeroPollsRemaining: false,
		});

		autorun(() => {
			const releasedOccupancies = occupancyModel.occupancies
				.filter(occupancy => occupancy.releaseAt);

			for (const releasedOccupancy of releasedOccupancies) {
				// Seed only entries that still have time on the clock. A backend-reported 0 means the grace period
				// has already elapsed and the backend's scheduled one-shot is racing to set occupiedUntil — we
				// handle that via post-zero polling instead of looping the 1s ticker on a zero entry.
				if (releasedOccupancy.gracePeriodSecondsLeft > 0) {
					this.gracePeriodSecondsLeft.set(releasedOccupancy.id, releasedOccupancy.gracePeriodSecondsLeft);
				}
			}
			if (releasedOccupancies.length > 0) {
				if (releasedOccupancies.some(o => o.gracePeriodSecondsLeft > 0)) {
					this.startCountDown();
				}
				else {
					this.startPostZeroPolling();
				}
			}
		});
	}

	getGracePeriodSecondsLeft(id: number) {
		return this.gracePeriodSecondsLeft.get(id);
	}

	private startCountDown() {
		if (!this.countDownInterval) {
			this.countDownInterval = window.setInterval(() => {
				runInAction(() => {
					for (let [id, secondsLeft] of this.gracePeriodSecondsLeft.entries()) {
						if (secondsLeft > 0) {
							this.gracePeriodSecondsLeft.set(id, secondsLeft - 1);
						}
						else {
							this.gracePeriodSecondsLeft.delete(id);
						}
						// re-fetch occupancy every 30 seconds
						if (secondsLeft % 30 == 0) {
							occupancyModel.fetch();
							cubeModel.fetch();
						}
					}
				});
				if (this.gracePeriodSecondsLeft.size == 0) {
					clearInterval(this.countDownInterval);
					this.countDownInterval = NaN;
					this.startPostZeroPolling();
				}
			}, 1000);
		}
	}

	// After the countdown hits 0, keep polling so the view advances when the backend confirms the end. The backend's
	// scheduled one-shot fires ~5s after graceEndsAt; once occupiedUntil is set, the occupancy drops out of the list
	// and OccupancyView unmounts.
	private startPostZeroPolling() {
		if (this.postZeroPollInterval) {
			return;
		}
		this.postZeroPollsRemaining = POST_ZERO_POLL_COUNT;
		this.postZeroPollInterval = window.setInterval(() => {
			const stillReleased = occupancyModel.occupancies.some(o => o.releaseAt);
			if (!stillReleased || this.postZeroPollsRemaining <= 0) {
				clearInterval(this.postZeroPollInterval);
				this.postZeroPollInterval = NaN;
				return;
			}
			this.postZeroPollsRemaining--;
			occupancyModel.fetch();
			cubeModel.fetch();
		}, POST_ZERO_POLL_INTERVAL_MS);
	}
}

export const gracePeriodModel = new GracePeriodModel();
